import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useRegenerateCoverImageMutation } from '../../../api/use-regenerate-cover-image-mutation';
import { validateCoverImageFile } from './coverImageValidation';
import { createBlobPreviewUrl, revokeBlobPreviewUrl } from './coverImageUtils';

export const useCoverImageOptimization = ({ form, onCoverImageChange }) => {
  const regenerateCoverImageMutation = useRegenerateCoverImageMutation();
  const abortControllerRef = useRef(null);
  const previousCoverRef = useRef(null);
  const localPreviewRef = useRef('');

  const [coverImageOptimizing, setCoverImageOptimizing] = useState(false);
  const [coverImageOptimizationError, setCoverImageOptimizationError] = useState('');
  const [coverImagePendingUpload, setCoverImagePendingUpload] = useState(false);

  const clearLocalPreview = useCallback(() => {
    if (localPreviewRef.current) {
      revokeBlobPreviewUrl(localPreviewRef.current);
      localPreviewRef.current = '';
    }
  }, []);

  const cancelCoverImageOptimization = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setCoverImageOptimizing(false);
    setCoverImageOptimizationError('');
    setCoverImagePendingUpload(false);

    clearLocalPreview();

    if (previousCoverRef.current) {
      onCoverImageChange(previousCoverRef.current);
    } else {
      onCoverImageChange({
        coverImageFile: null,
        coverImagePreview: '',
      });
    }

    previousCoverRef.current = null;
  }, [clearLocalPreview, onCoverImageChange]);

  const handleCoverImageSelected = useCallback(
    async (file) => {
      const validation = validateCoverImageFile(file);
      if (!validation.valid) {
        toast.error(validation.message || 'Invalid cover image.');
        return;
      }

      previousCoverRef.current = {
        coverImageFile: form.coverImageFile,
        coverImagePreview: form.coverImagePreview,
      };

      clearLocalPreview();

      const localPreview = createBlobPreviewUrl(file);
      localPreviewRef.current = localPreview;

      onCoverImageChange({
        coverImageFile: null,
        coverImagePreview: localPreview,
      });

      setCoverImageOptimizing(true);
      setCoverImageOptimizationError('');
      setCoverImagePendingUpload(true);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const optimizationToastId = 'cover-image-optimization';
      toast.loading('Optimizing cover image… This may take up to 2 minutes.', {
        id: optimizationToastId,
      });

      try {
        const optimizedFile = await regenerateCoverImageMutation.mutateAsync({
          file,
          signal: controller.signal,
        });

        const optimizedPreview = createBlobPreviewUrl(optimizedFile);
        clearLocalPreview();

        onCoverImageChange({
          coverImageFile: optimizedFile,
          coverImagePreview: optimizedPreview,
        });

        setCoverImageOptimizing(false);
        setCoverImagePendingUpload(false);
        previousCoverRef.current = null;
        toast.success('Cover image optimized successfully.', { id: optimizationToastId });
      } catch (error) {
        if (axios.isCancel(error)) {
          toast.dismiss(optimizationToastId);
          return;
        }

        setCoverImageOptimizing(false);
        setCoverImageOptimizationError(error?.message || 'Failed to optimize cover image.');
        toast.error(error?.message || 'Failed to optimize cover image.', {
          id: optimizationToastId,
        });
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [
      clearLocalPreview,
      form.coverImageFile,
      form.coverImagePreview,
      onCoverImageChange,
      regenerateCoverImageMutation,
    ],
  );

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
      clearLocalPreview();
    },
    [clearLocalPreview],
  );

  return {
    coverImageOptimizing,
    coverImageOptimizationError,
    coverImagePendingUpload,
    handleCoverImageSelected,
    cancelCoverImageOptimization,
  };
};
